/* eslint-disable react/require-default-props */
import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  currencyFormatter,
  PageHeader,
} from '@cloudcar-app/cloudcar-components';
import { Row, Col } from 'antd';
import { useQuery, useReactiveVar } from '@apollo/client';
import operations from 'apollo/operations';
import {
  Car,
  InsuranceUseType,
  LitePurchaseIntent,
  PaymentType,
} from 'apollo/types';
import { stepList } from 'apollo/cache';
import dateFormatter from 'utils/dateFormatter';
import tagPriceMapper from 'utils/kaufmann/dealershipMapper';
import { vehicleTypes } from 'utils/kaufmann/vehicleTypesMappers';
import getStepIndexByPathname from 'utils/getStepIndexByPathname';
import styles from './Header.module.scss';

interface HeaderProps {
  headerAlert?: JSX.Element;
  downloadButton?: JSX.Element;
}

const Header: FC<HeaderProps> = (props: HeaderProps) => {
  const { headerAlert, downloadButton } = props;

  const history = useHistory();
  const steps = useReactiveVar(stepList);
  const currentStepIndex = getStepIndexByPathname(
    history.location.pathname,
    steps,
  );

  const [selectedDealership, setSelectedDealership] = useState<string>(
    tagPriceMapper[0].value,
  );
  const [selectedVehicleType, setSelectedVehicleType] = useState<string>(
    vehicleTypes[0].value,
  );

  const { data: litePurchaseIntentData } = useQuery<{
    unitedLitePurchaseIntent: LitePurchaseIntent;
  }>(operations.GET_LITE_PURCHASE_INTENT, {
    fetchPolicy: 'cache-and-network',
  });

  const paymentTypeMapper = {
    [PaymentType.Cash]: 'Contado',
    [PaymentType.Installments]: 'Tarjeta de Crédito',
    [PaymentType.Pac]: 'PAC',
  };

  const renderCarInfo = (car?: Car) => {
    if (car && car.model && car.brand) {
      return `${car.brand} ${car.model} ${car.year || ''}`;
    }
    return '-';
  };

  useEffect(() => {
    const dealer = localStorage.getItem('dealer');
    const dealershipOptionsKeys = tagPriceMapper.map((option) => option.value);
    const vehicleTypesKeys = vehicleTypes.map((option) => option.value);
    let dealershipValue = tagPriceMapper[0].value;
    let vehicleTypeValue = vehicleTypes[0].value;

    if (dealer && dealershipOptionsKeys.includes(dealer)) {
      dealershipValue = dealer;
    }
    setSelectedDealership(dealershipValue);

    let vehicleType;

    if (litePurchaseIntentData?.unitedLitePurchaseIntent.car) {
      vehicleType =
        litePurchaseIntentData?.unitedLitePurchaseIntent.car?.vehicleType;
    }

    if (vehicleType && vehicleTypesKeys.includes(vehicleType)) {
      vehicleTypeValue = vehicleType;
    }
    setSelectedVehicleType(vehicleTypeValue);
  }, [litePurchaseIntentData]);

  return (
    <div>
      {headerAlert || null}
      <Row justify="space-between" align="middle">
        <PageHeader
          title={`Detalle de solicitud - ${
            litePurchaseIntentData?.unitedLitePurchaseIntent?.car?.createdAt
              ? dateFormatter(
                  litePurchaseIntentData?.unitedLitePurchaseIntent?.car
                    ?.createdAt,
                )
              : ''
          }`}
          onBack={() => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            steps[currentStepIndex - 1]
              ? history.push({
                  pathname: steps[currentStepIndex - 1].route,

                  state: {
                    from: history.location,
                  },
                })
              : history.goBack();
          }}
        />
        {downloadButton || null}
      </Row>
      <Row className={styles.ProceduresRow} gutter={[24, 24]}>
        <Col span={8}>
          {litePurchaseIntentData?.unitedLitePurchaseIntent?.quotationId && (
            <Row justify="space-between">
              <p>N° cotización</p>
              <p>
                {litePurchaseIntentData?.unitedLitePurchaseIntent?.quotationId}
              </p>
            </Row>
          )}
          {litePurchaseIntentData?.unitedLitePurchaseIntent?.car?.model && (
            <Row justify="space-between">
              <p>Vehículo</p>
              <p>
                {renderCarInfo(
                  litePurchaseIntentData?.unitedLitePurchaseIntent?.car,
                )}
              </p>
            </Row>
          )}

          {litePurchaseIntentData?.unitedLitePurchaseIntent?.car?.price && (
            <Row justify="space-between">
              <p>Valor neto factura</p>
              <p>
                {currencyFormatter().format(
                  litePurchaseIntentData?.unitedLitePurchaseIntent?.car
                    ?.price || 0,
                )}
              </p>
            </Row>
          )}
          {litePurchaseIntentData?.unitedLitePurchaseIntent?.car?.cit && (
            <Row justify="space-between">
              <p>CIT</p>
              <p>
                {litePurchaseIntentData?.unitedLitePurchaseIntent?.car?.cit}
              </p>
            </Row>
          )}
          <Row justify="space-between">
            <p>Tipo de vehículo</p>
            <p>{selectedVehicleType}</p>
          </Row>
          {litePurchaseIntentData?.unitedLitePurchaseIntent?.insurance
            ?.insuranceInput?.carPlate && (
            <Row justify="space-between">
              <p>Patente (semi nuevo)</p>
              <p>
                {
                  litePurchaseIntentData?.unitedLitePurchaseIntent?.insurance
                    .insuranceInput.carPlate
                }
              </p>
            </Row>
          )}
          {localStorage.getItem('customerRut') && (
            <Row justify="space-between">
              <p>Rut cliente</p>
              <p>{localStorage.getItem('customerRut')}</p>
            </Row>
          )}
          <Row justify="space-between">
            <p>Sucursal</p>
            <p>{selectedDealership}</p>
          </Row>
          {litePurchaseIntentData?.unitedLitePurchaseIntent?.insurance && (
            <Row justify="space-between">
              <p>Tipo de uso</p>
              <p>
                {litePurchaseIntentData?.unitedLitePurchaseIntent?.insurance
                  .insuranceInput.useType === InsuranceUseType.Commercial
                  ? 'Comercial'
                  : 'Particular'}
              </p>
            </Row>
          )}
          {litePurchaseIntentData?.unitedLitePurchaseIntent?.insurance && (
            <Row justify="space-between">
              <p>Tipo de pago</p>
              <p>
                {
                  paymentTypeMapper[
                    litePurchaseIntentData?.unitedLitePurchaseIntent?.insurance
                      .insuranceInput.paymentType
                  ]
                }
              </p>
            </Row>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Header;
